import React, { createContext, useContext, useEffect, useState } from "react";
import type { UserAuthDto } from "@procureflow/shared-types";
import {
  bootstrapSession,
  loginApi,
  logoutApi,
  getSession,
  setSession as writeSession,
  subscribeSession,
  consumeExpirationFlag,
  fetchWithAuth,
  type AuthSession,
} from "@/lib/auth-client";
import { effectivePermissions, type ResolvedPermissions } from "@/lib/roles";

type SessionExpiredReason = "session_expired" | null;

interface AuthContextValue {
  user: UserAuthDto | null;
  /**
   * The signed-in user's resolved permission set (ADR-017 §3), fetched
   * from /api/me/permissions. Until that settles — or if it fails — this
   * falls back to the static manifest for the user's role, so gating is
   * never undefined and seeded roles behave identically to before.
   */
  permissions: ResolvedPermissions;
  /** Set when fetchWithAuth had to expire the session (refresh failed). Cleared on next login. */
  sessionExpiredReason: SessionExpiredReason;
  /** True until the initial /api/me bootstrap settles. */
  isBootstrapping: boolean;
  /**
   * Authenticates and returns the resolved session so the caller can route
   * by the user (US-055: a must_change_password account must land on
   * /change-password). Keeping that decision at the login call site avoids
   * racing the AppLayout gate's <Navigate to="/change-password">.
   */
  login: (email: string, password: string) => Promise<AuthSession>;
  logout: () => Promise<void>;
}

async function loadPermissions(): Promise<ResolvedPermissions | null> {
  try {
    const res = await fetchWithAuth("/api/me/permissions");
    if (!res.ok) return null;
    const body = (await res.json()) as { data: ResolvedPermissions };
    return body.data;
  } catch {
    return null;
  }
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Synchronous init from the in-memory / legacy-localStorage store.
  // The /api/me bootstrap below upgrades this to the authoritative
  // server-resolved identity (or clears it on 401).
  const [session, setSessionState] = useState<AuthSession | null>(() => getSession());
  const [sessionExpiredReason, setSessionExpiredReason] = useState<SessionExpiredReason>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [fetchedPermissions, setFetchedPermissions] = useState<ResolvedPermissions | null>(null);

  useEffect(() => {
    return subscribeSession((next) => {
      setSessionState(next);
      if (next === null && consumeExpirationFlag()) {
        setSessionExpiredReason("session_expired");
      } else if (next !== null) {
        setSessionExpiredReason(null);
      }
    });
  }, []);

  // ADR-016 bootstrap: GET /api/me on app boot.
  //
  // Cookies (pf_access) are browser-attached automatically. The endpoint
  // returns the resolved user on 200; 401 means no session — render the
  // unauthenticated tree. Legacy localStorage-injected sessions still
  // populate `session` synchronously via getSession() in useState above,
  // so tests that inject `pf_session` see the user immediately and the
  // /api/me probe simply confirms it (or 401s harmlessly).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // In dev, MSW activates asynchronously; wait for it before the
        // first /api/me probe (otherwise the probe escapes to the network
        // and the FE briefly thinks the user is logged out).
        if (import.meta.env.DEV) {
          const { mswReady } = await import("@/mocks/browser");
          await mswReady;
        }
        await bootstrapSession();
      } finally {
        if (!cancelled) setIsBootstrapping(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // ADR-017 §3 — fetch the resolved permission set whenever the signed-in
  // user changes (initial session, login, logout). Keyed on user id so a
  // role edit is reflected on the next bootstrap/login without a redeploy.
  // Awaits mswReady in dev so the first fetch doesn't escape the SW.
  const userId = session?.user?.id ?? null;
  useEffect(() => {
    let cancelled = false;
    if (!userId) {
      setFetchedPermissions(null);
      return;
    }
    (async () => {
      if (import.meta.env.DEV) {
        const { mswReady } = await import("@/mocks/browser");
        await mswReady;
      }
      const perms = await loadPermissions();
      if (!cancelled) setFetchedPermissions(perms);
    })();
    return () => {
      cancelled = true;
    };
  }, [userId]);

  async function login(email: string, password: string): Promise<AuthSession> {
    const data = await loginApi(email, password);
    writeSession(data);
    return data;
  }

  async function logout() {
    await logoutApi();
    writeSession(null);
  }

  return (
    <AuthContext.Provider
      value={{
        user: session?.user ?? null,
        permissions: effectivePermissions(session?.user ?? null, fetchedPermissions),
        sessionExpiredReason,
        isBootstrapping,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
